import React from "react";
import { Link } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { AdminTopNav } from "@/components/admin/AdminTopNav";
import { adminGet } from "@/lib/admin-api";

interface DonorRow {
  id: string;
  email: string;
  displayName: string;
  totalGiftsCount: number;
  totalGiftsAmountUsd: number;
  lastGiftAt: string | null;
  recognitionConsent: "anonymous" | "named" | "unspecified";
  recognitionDisplayName?: string;
  address?: { city?: string; country?: string };
}

function DonorsAdminInner() {
  const [items, setItems] = React.useState<DonorRow[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState<string>("");

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const url = search
        ? `/api/admin/donors/list?search=${encodeURIComponent(search)}`
        : "/api/admin/donors/list";
      const res = await adminGet<{ items: DonorRow[] }>(url);
      setItems(res.items);
    } catch (err) {
      setError(String(err));
    }
  }, [search]);

  React.useEffect(() => {
    void load();
  }, [load]);

  return (
    <PageShell title="Donors · Admin" status="admin" width="wide">
      <Seo title="Donors admin" description="" path="/admin/donors" noindex />
      <AdminTopNav />
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
        <h2 className="font-serif text-xl text-ink">Donors</h2>
        <input
          type="search"
          placeholder="Search by name, email, company"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-hairline bg-paper px-3 py-2 font-mono text-xs w-72"
        />
      </div>

      {error && (
        <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500 mb-6">
          {error}
        </div>
      )}

      {items === null && !error && (
        <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">Loading…</p>
      )}

      {items && items.length === 0 && (
        <p className="font-serif text-base text-ink-2 leading-relaxed">
          No donors yet. As gifts arrive, donors will appear here.
        </p>
      )}

      {items && items.length > 0 && (
        <div className="border border-hairline overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-paper-2 text-[10px] font-mono uppercase tracking-widest text-ink-3">
              <tr className="border-b border-hairline">
                <th className="text-left px-4 py-3 font-bold">Donor</th>
                <th className="text-left px-4 py-3 font-bold">Email</th>
                <th className="text-right px-4 py-3 font-bold">Gifts</th>
                <th className="text-right px-4 py-3 font-bold">Total (USD)</th>
                <th className="text-left px-4 py-3 font-bold">Last gift</th>
                <th className="text-left px-4 py-3 font-bold">Recognition</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline text-ink-2">
              {items.map((d) => (
                <tr key={d.id} className="hover:bg-paper-2">
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/donors/${encodeURIComponent(d.id)}`}
                      className="text-ink hover:text-claude underline decoration-hairline hover:decoration-ink"
                    >
                      {d.displayName || d.email}
                    </Link>
                    {d.address?.city && (
                      <span className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest">
                        {d.address.city}
                        {d.address.country ? `, ${d.address.country}` : ""}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{d.email}</td>
                  <td className="px-4 py-3 font-mono text-xs text-right">{d.totalGiftsCount}</td>
                  <td className="px-4 py-3 font-mono text-xs text-right">
                    {d.totalGiftsAmountUsd.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest">
                    {d.lastGiftAt ? new Date(d.lastGiftAt).toLocaleDateString("en-GB") : "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest">
                    {d.recognitionConsent === "named"
                      ? d.recognitionDisplayName || "Named"
                      : d.recognitionConsent === "anonymous"
                        ? "Anonymous"
                        : "Unspecified"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}

export function DonorsAdmin() {
  return (
    <AdminGuard allow={["admin"]}>
      <DonorsAdminInner />
    </AdminGuard>
  );
}
